/**
 * v37. 라이브 바이패스의 **진입 판정** (`docs/75` §5.1 · 절대 규칙 99).
 *
 * v33·v34 에서는 이 판정이 `BloomReceiver` 의 비공개 메서드 안(`maybeEnterLive`·`prepareLiveCut`·
 * 적재 콜백)에 있었다. Flower 엔진이 같은 판정을 Kotlin 으로 하므로 **동작 그대로** 꺼냈다 —
 * 결정만 여기 있고 실행(적재·무장·스왑)은 리시버에 남는다. Kotlin 은 이 함수들을 불러 뽑은
 * 벡터(`vectors/v37/live-entry.json`)로 맞춘다(규칙 95).
 */
import { AD_REQUEST_LEAD_SEC, liveWindowAt } from '@apm/contracts';
/**
 * ★ v33-c. **무장한 컷을 기다려 주는 상한**(초) — **무장한 순간부터** 센다(v33-d).
 *
 * 컷은 무장한 경과를 넘어선 **첫 청크 경계**라 최대 한 청크(2초) 뒤다. 그만큼은 기다리되,
 * 스왑이 조용히 실패하면(`armTailSwap` 은 컷을 구하지 못하면 아무 말 없이 거른다)
 * **즉시형이 받아 간다** — 시청자가 라이브를 영영 못 보는 것보다 2초 늦은 편이 낫다.
 */
export const LIVE_CUT_WAIT_SEC = 3;
/**
 * ★ v33-d. **라이브 버퍼의 적재를 기다려 주는 상한**(초 · 2026-09-23 · `docs/68` §2.0-e).
 *
 * 창이 열린 뒤에 적재를 시작하고, 적재가 **끝나야** 컷을 무장한다. 실측 적재는 1초 안이다
 * (AWS · 홈을 지나 창 중간에 들어간 길). 이 안에 안 끝나면 즉시형이 받는다.
 */
export const LIVE_PREPARE_WAIT_SEC = 6;
/** 적재가 실패했을 때 다시 시도하기까지(ms). 4Hz 틱마다 두드리지 않게 한다 */
export const LIVE_PREPARE_RETRY_MS = 1000;
/**
 * 바이패스에 들어갈 때인가. **`onTimeUpdate` 축(약 4Hz)에서 부른다** — 예약형 광고와 같은 축이다.
 *
 * ① **광고가 화면에 있다** → 즉시형(광고 버퍼에 컷을 걸 수 없고 pod 은 버린다 · `docs/67` D6)
 * ② **본편이 화면에 있다** → 적재하고, **끝나면** 미드롤과 물리적으로 같은 컷을 무장한다
 *
 * **창이 열리기 전에는 아무것도 하지 않는다** — 창 전의 스트림 주소는 503 이다(`docs/68` §2.0-e).
 * **무장한 컷을 앞지르지 않는다** — 그 사이 즉시형이 들어가면 컷은 영영 안 터지고 복귀 지점이 청크
 * 경계가 아니게 된다(`docs/68` §2.1). 기다림에는 상한이 있다(적재 6초 · 무장 3초).
 */
export function liveEntryPlan(input) {
    if (input.state !== 'playing')
        return { action: 'none', window: null };
    if (input.inLive)
        return { action: 'none', window: null };
    const { current } = liveWindowAt(input.windows, input.nowMs);
    if (current === null || input.skippedEventIds.has(current.eventId)) {
        return { action: 'none', window: null };
    }
    if (input.adOnScreen)
        return { action: input.adLeadsToLive ? 'wait' : 'enter-now', window: current };
    if (input.adPending)
        return { action: 'wait', window: current };
    const cut = input.cut;
    if (cut !== null && cut.eventId === current.eventId) {
        const waitedSec = (input.nowMs - cut.sinceMs) / 1000;
        const limitSec = cut.phase === 'preparing' ? LIVE_PREPARE_WAIT_SEC : LIVE_CUT_WAIT_SEC;
        return { action: waitedSec < limitSec ? 'wait' : 'enter-now', window: current };
    }
    if (input.nowMs < input.retryAtMs)
        return { action: 'wait', window: current };
    // 회차 끝이 가까우면 기다린다 — 적재가 대기 버퍼를 라이브로 갈아 끼우므로 그 사이에 회차가 끝나면
    // 자연 전환이 라이브로 넘어간다. 다음 회차가 시작된 뒤에 자른다(합류가 몇 초 늦을 뿐이다).
    if (input.item === null)
        return { action: 'none', window: null };
    const remainSec = input.item.durationSec - input.elapsedSec;
    if (input.item.durationSec > 0 && remainSec < LIVE_PREPARE_WAIT_SEC + LIVE_CUT_WAIT_SEC) {
        return { action: 'defer', window: current };
    }
    return { action: 'prepare', window: current };
}
/**
 * 적재가 끝났다 — **컷을 무장해도 되는가.** 기다리는 사이에 무엇이 바뀌었으면 이 적재는 버린다
 * (다음 틱이 다시 판단한다).
 */
export function liveArmAllowed(input) {
    return (!input.inLive &&
        !input.adOnScreen &&
        !input.adPending &&
        input.sameItem &&
        input.state === 'playing');
}
// ── 2026-10-29 · 라이브 시작 전 광고 (`docs/45` §18) ─────────────────────────
/**
 * 시각 방식 이벤트의 컷을 **시작 시각 − 받은 광고 길이 − 이만큼**에 무장한다(ms). 컷은 무장한 경과를 넘어선 첫 청크 경계(최대 2초 뒤)라,
 * 한가운데로 1초를 당겨 브레이크의 끝이 시작 시각 둘레(−1~+1초)에 오게 한다.
 */
export const LIVE_BEFORE_CUT_LEAD_MS = 1000;
/** 시각 방식 이벤트는 시작까지 이만큼 남아야 묻는다(ms) — 응답·컷·적재가 들어갈 자리 */
export const LIVE_BEFORE_MIN_LEAD_MS = 3000;
const NONE = { action: 'none', window: null, blocking: false };
/**
 * 라이브 시작 전 광고의 때 — **`onTimeUpdate` 축(약 4Hz)에서 `liveEntryPlan` 앞에 부른다.** 결정만 여기 있고 요청·컷은 리시버가 한다
 * (Flower 엔진이 같은 표로 맞춘다 · 규칙 95).
 *
 * - **시각 방식**: 시작 `breakSec + AD_REQUEST_LEAD_SEC` 초 전부터 묻는다(시작까지 `LIVE_BEFORE_MIN_LEAD_MS` 이상 남았을 때만). 받은 포드의
 *   길이 합만큼 시작 전(− `LIVE_BEFORE_CUT_LEAD_MS`)에 자른다 — 브레이크가 시작 순간 둘레에서 끝난다. 컷 전에 시작했으면 버린다.
 * - **개시 큐**: 창이 열렸는데(큐가 왔다) 녹화물이 화면에 있으면 곧바로 묻고(즉시형 타임아웃), 받으면 곧바로 자른다. 그동안
 *   `liveEntryPlan` 은 `adPending` 으로 기다린다 — 리시버가 묻는 중·받아 둔 포드를 「대기」로 넘긴다.
 */
export function liveBeforePlan(input) {
    if (input.breakSec <= 0 || !input.onContent)
        return NONE;
    const pod = input.pod;
    if (pod !== null) {
        const window = input.windows.find((w) => w.eventId === pod.eventId) ?? null;
        if (window === null || input.skippedEventIds.has(pod.eventId))
            return { action: 'drop', window, blocking: false };
        if (window.startMode === 'cue') {
            // 개시 큐의 포드는 창이 열린 뒤에만 생긴다 — 그 창이 더 열려 있지 않으면 버린다(남기면 다음 이벤트의 진입을 막는다)
            const { current } = liveWindowAt(input.windows, input.nowMs);
            return { action: current?.eventId === pod.eventId ? 'cut' : 'drop', window, blocking: false };
        }
        if (input.nowMs >= window.startAtMs)
            return { action: 'drop', window, blocking: false };
        const cutAtMs = window.startAtMs - pod.totalSec * 1000 - LIVE_BEFORE_CUT_LEAD_MS;
        return input.nowMs >= cutAtMs ? { action: 'cut', window, blocking: false } : NONE;
    }
    const fresh = (w) => !input.askedEventIds.has(w.eventId) && !input.skippedEventIds.has(w.eventId);
    // 개시 큐 — 창이 방금 열렸다(녹화물을 보던 시청자에게만 · 이미 진행 중에 들어온 시청자는 녹화물이 화면에 없다)
    const { current } = liveWindowAt(input.windows, input.nowMs);
    if (current !== null && current.startMode === 'cue' && fresh(current)) {
        return { action: 'request', window: current, blocking: true };
    }
    // 시각 방식 — 시작 앞, 가장 가까운 것
    let next = null;
    for (const w of input.windows) {
        if (w.startMode !== 'time' || !fresh(w))
            continue;
        const leadMs = w.startAtMs - input.nowMs;
        if (leadMs < LIVE_BEFORE_MIN_LEAD_MS)
            continue;
        if (leadMs > (input.breakSec + AD_REQUEST_LEAD_SEC) * 1000)
            continue;
        if (next === null || w.startAtMs < next.startAtMs)
            next = w;
    }
    return next === null ? NONE : { action: 'request', window: next, blocking: false };
}
/**
 * **녹화물의 광고 브레이크를 지금 열지 않는가** — 브레이크의 관문(BLOOM `openBreak` · Flower `ChannelSession`)이 최소 간격 옆에서 부른다.
 * 거르는 것이지 미루는 것이 아니다(최소 간격과 같다 · 회차 사이면 광고 없이 다음 회차다).
 *
 * - **뒤**: 라이브 앞뒤 광고가 끝나고 `gapSec` 이 안 지났다 — 라이브에서 광고를 보고 녹화물로 오자마자 또 광고를 보지 않는다.
 * - **앞**: 시각 방식 이벤트의 시작 전 광고가 다가온다 — 이 브레이크가 가장 늦게 끝나는 때(지금 + 요청 앞섬 + 길이) + `gapSec` 이 그
 *   광고를 묻는 시각(시작 − 시작 전 광고 − 요청 앞섬)을 넘는다. **시작 전 광고가 이긴다** — 녹화물 광고가 그 자리를 차지하면 시작 전
 *   광고는 묻지도 못하고(녹화물이 화면에 없다) 시청자는 광고 하나를 보다가 라이브로 끌려간다(2026-10-29 운영 신고). 이미 시작한 창 ·
 *   시청자가 두고 나온 창은 지킬 것이 없다.
 */
export function recordedBreakQuiet(input) {
    const gapMs = Math.max(0, input.gapSec) * 1000;
    if (gapMs > 0 &&
        input.lastLiveAdEndedAtMs !== null &&
        input.nowMs - input.lastLiveAdEndedAtMs < gapMs) {
        return true;
    }
    if (input.liveBeforeSec <= 0)
        return false;
    const reachMs = (AD_REQUEST_LEAD_SEC + Math.max(0, input.breakSec)) * 1000 + gapMs;
    for (const w of input.windows) {
        if (w.startMode !== 'time' || input.skippedEventIds.has(w.eventId))
            continue;
        if (input.nowMs >= w.startAtMs)
            continue;
        const askAtMs = w.startAtMs - (input.liveBeforeSec + AD_REQUEST_LEAD_SEC) * 1000;
        if (input.nowMs + reachMs > askAtMs)
            return true;
    }
    return false;
}
/**
 * **라이브 앞뒤 광고 중에는 같은 채널의 녹화물로 가지 않는다**(채널 정책 `liveAdLockRecorded` · `docs/45` §18.8). 두 엔진의 회차 선택·
 * 건너뛰기가 이것이 참이면 무동작이다 — 화면은 같은 판정으로 알림을 띄운다. 채널 전환·홈은 산다.
 */
export function recordedLockedByLiveAd(lockRecorded, breakKind) {
    return lockRecorded && (breakKind === 'live_before' || breakKind === 'live_after');
}
//# sourceMappingURL=live-entry.js.map